import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Separator } from './ui/separator';
import { 
  FileText, 
  Image as ImageIcon, 
  File, 
  Download, 
  Eye, 
  Trash2,
  Loader2,
  Sparkles,
  AlertCircle
} from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import { DataMappingVisualization } from './DataMappingVisualization';

interface CIAttachmentListProps {
  companyId: string;
  configItemId: string;
  accessToken: string;
  onDelete?: (attachmentId: string) => void;
}

interface Attachment {
  id: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  uploadedBy: {
    userId: string;
    userEmail: string;
    userName?: string;
  };
  uploadedAt: string;
  signedUrl?: string;
  aiAnalysis?: any;
}

export function CIAttachmentList({
  companyId,
  configItemId,
  accessToken,
  onDelete
}: CIAttachmentListProps) {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAttachment, setSelectedAttachment] = useState<Attachment | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const loadAttachments = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/attachments/${companyId}?configItemId=${configItemId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAttachments(data.attachments || []);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to load attachments');
      }
    } catch (error) {
      console.error('Error loading attachments:', error);
      setError('Failed to load attachments');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (attachmentId: string) => {
    if (!confirm('Are you sure you want to delete this file?')) {
      return;
    }

    setDeleting(attachmentId);

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/attachment/${attachmentId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        setAttachments(attachments.filter(att => att.id !== attachmentId));
        if (onDelete) {
          onDelete(attachmentId);
        }
      } else {
        alert('Failed to delete file');
      }
    } catch (error) {
      console.error('Error deleting attachment:', error);
      alert('Failed to delete file');
    } finally {
      setDeleting(null);
    }
  };

  useEffect(() => {
    loadAttachments();
  }, [companyId, configItemId, accessToken]);

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) {
      return <ImageIcon className="h-5 w-5" />;
    } else if (fileType.includes('pdf')) {
      return <FileText className="h-5 w-5" />;
    } else {
      return <File className="h-5 w-5" />;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Loading attachments...</span>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (attachments.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <File className="h-12 w-12 mx-auto mb-2 text-muted-foreground" />
          <p className="text-muted-foreground">No files uploaded yet</p>
          <p className="text-sm text-muted-foreground mt-1">
            Upload files above to get started
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <File className="h-5 w-5" />
            Uploaded Files ({attachments.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {attachments.map((attachment) => (
            <div key={attachment.id}>
              <div className="flex items-start gap-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                <div className="mt-1">
                  {getFileIcon(attachment.fileType)}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{attachment.fileName}</p>
                      <p className="text-sm text-muted-foreground">
                        {formatFileSize(attachment.fileSize)}
                      </p>
                    </div>

                    {attachment.aiAnalysis && (
                      <Badge variant="secondary" className="flex items-center gap-1 shrink-0">
                        <Sparkles className="h-3 w-3" />
                        AI Analyzed
                      </Badge>
                    )}
                  </div>

                  <p className="text-xs text-muted-foreground mt-1">
                    Uploaded by {attachment.uploadedBy.userName || attachment.uploadedBy.userEmail}
                    {' • '}
                    {formatDate(attachment.uploadedAt)}
                  </p>

                  {attachment.aiAnalysis && (
                    <div className="mt-2">
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs"
                        onClick={() => setSelectedAttachment(
                          selectedAttachment?.id === attachment.id ? null : attachment
                        )}
                      >
                        {selectedAttachment?.id === attachment.id ? 'Hide' : 'View'} AI Analysis
                      </Button>
                    </div>
                  )}
                </div>

                <div className="flex gap-2 shrink-0">
                  {attachment.signedUrl && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(attachment.signedUrl, '_blank')}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const a = document.createElement('a');
                          a.href = attachment.signedUrl!;
                          a.download = attachment.fileName;
                          a.click();
                        }}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(attachment.id)}
                    disabled={deleting === attachment.id}
                  >
                    {deleting === attachment.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4 text-destructive" />
                    )}
                  </Button>
                </div>
              </div>

              {/* AI Analysis Visualization */}
              {selectedAttachment?.id === attachment.id && attachment.aiAnalysis && (
                <div className="mt-3 pl-3">
                  <DataMappingVisualization
                    attachment={attachment}
                    onAcceptMapping={(suggestion) => {
                      console.log('Accepted mapping:', suggestion);
                      // TODO: Store accepted mappings
                    }}
                    onRejectMapping={(suggestion) => {
                      console.log('Rejected mapping:', suggestion);
                      // TODO: Store rejected mappings
                    }}
                  />
                </div>
              )}

              {selectedAttachment?.id === attachment.id && !attachment.aiAnalysis && (
                <Alert className="mt-3 ml-3">
                  <AlertDescription>
                    No AI analysis available for this file.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
